import BellOnOutlineIcon from '@/assets/bell-on-outline.svg?react'
import NavBar from '@/components/NavBar'
import Page from '@/components/Page'
import PageLoading from '@/components/PageLoading'
import { Tab } from '@/components/Sidebar/config'
import { MOBILE_SHELL_SPRING_TRANSITION } from '@/constants/motion'
import { useIsMobileShellHidden, useScrollToTop } from '@/utils/dom'
import {
	getIsNotiAllowed,
	NOTIFICATIONS_QUERY_OPTIONS,
	setIsNotiAllowed,
	UNREAD_NOTIFICATION_COUNT_QUERY_OPTIONS,
	useIsNotiAllowed,
} from '@/utils/fetchers/notification'
import { useIsMobileTabBarLayout } from '@/utils/hooks/useMediaQuery'
import usePageNeedRefreshSubscriber from '@/utils/hooks/usePageNeedRefreshSubscriber'
import { prepareToRefetchListQuery } from '@/utils/list'
import { sleep } from '@/utils/promise'
import queryClient from '@/utils/queryClient'
import { Button } from '@mantine/core'
import { useQuery, useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { flatMap } from 'lodash-es'
import { motion } from 'motion/react'
import { type HTMLAttributes, useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import { useMount, useTitle } from 'react-use'
import NotificationList from '../components/Notifications/NotificationList'
import styles from './notification.module.css'

const MOBILE_NAV_BAR_HEIGHT = 'calc(62px + env(safe-area-inset-top, 0px))'

export const Route = createFileRoute('/notification')({
	component: NotificationPage,
	pendingComponent: () => <PageLoading />,
	loader: async () => {
		await queryClient.ensureInfiniteQueryData(NOTIFICATIONS_QUERY_OPTIONS)
	},
	preload: false,
})

export interface NotificationsPageProps extends HTMLAttributes<HTMLDivElement> {}

export default function NotificationPage({ ...props }: NotificationsPageProps) {
	useTitle(`通知 - 即刻`)

	const {
		data: queryData,
		fetchNextPage,
		refetch,
		isFetchingNextPage,
	} = useSuspenseInfiniteQuery(NOTIFICATIONS_QUERY_OPTIONS)
	const { data: unreadCount = 0 } = useQuery(UNREAD_NOTIFICATION_COUNT_QUERY_OPTIONS)
	const data = useMemo(() => [...flatMap(queryData?.pages, 'data')], [queryData])
	const scrollToTop = useScrollToTop()
	const isMobileShellHidden = useIsMobileShellHidden()
	const isMobileTabBarLayout = useIsMobileTabBarLayout()
	const isNavBarHidden = isMobileTabBarLayout && isMobileShellHidden

	const refresh = useCallback(async () => {
		prepareToRefetchListQuery(NOTIFICATIONS_QUERY_OPTIONS.queryKey)
		return refetch()
			.then(() =>
				queryClient.invalidateQueries({
					queryKey: UNREAD_NOTIFICATION_COUNT_QUERY_OPTIONS.queryKey,
				})
			)
			.then(() => {
				toast.success('加载成功')
			})
	}, [refetch])

	const [isNotiAllow] = useIsNotiAllowed()
	const handleNotiAllowanceChange = useCallback(() => {
		const isAllow = getIsNotiAllowed()
		if (isAllow) {
			setIsNotiAllowed(false)
			toast('通知已关闭')
		} else {
			Notification.requestPermission().then((permission) => {
				const isGranted = permission === 'granted'
				setIsNotiAllowed(isGranted)
				toast(isGranted ? '通知已开启' : '通知已关闭')
			})
		}
	}, [])

	useMount(() => {
		if (unreadCount > 0) {
			sleep(1000)
				.then(() => scrollToTop())
				.then(() => sleep(500))
				.then(() => refresh())
		}
	})

	usePageNeedRefreshSubscriber(Tab.NOTIFICATION, () => {
		scrollToTop()
		refresh()
	})

	return (
		<Page
			header={
				<NavBar
					title="通知"
					showBackButton={false}
					renderRightSection={() => (
						<Button
							variant={isNotiAllow ? 'filled' : 'outline'}
							size="sm"
							className={styles.button}
							aria-label={isNotiAllow ? '关闭通知' : '开启通知'}
							title={isNotiAllow ? '关闭通知' : '开启通知'}
							onClick={handleNotiAllowanceChange}
						>
							<BellOnOutlineIcon color="currentColor" width={20} height={20} />
						</Button>
					)}
				/>
			}
		>
			<motion.div
				className={styles.listWrapper}
				initial={false}
				animate={{
					y: isNavBarHidden ? `calc(-1 * ${MOBILE_NAV_BAR_HEIGHT})` : '0px',
				}}
				transition={MOBILE_SHELL_SPRING_TRANSITION}
			>
				<NotificationList
					data={data}
					isLoading={isFetchingNextPage}
					onLoadMore={async () => void (await fetchNextPage())}
				/>
			</motion.div>
		</Page>
	)
}
